import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay, map, catchError, switchMap, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ERROR_DISPLAY_DURATION } from '../constants/http-context-tokens';
import {
  ServiceTypeMappingRequest,
  ServiceTypeMappingResponse,
} from '../../features/customer/integrations/components/forms/connectwise/models/service-type-mapping.models';

@Injectable({
  providedIn: 'root',
})
export class ConnectWiseService {
  private http = inject(HttpClient);
  // Flag to enable test mode (bypass authentication)
  private testMode = true;
  // ConnectWise client ID
  private readonly CONNECTWISE_CLIENT_ID = 'ff32be87-2ebb-4360-81d2-907a5b57f4bb';
  private configurationStatuses$: Observable<any[]> | null = null;

  constructor() {}

  // API call to test connection
  testConnection(credentials: any): Observable<{ success: boolean; message: string }> {
    // In test mode, always return success
    if (this.testMode) {
      console.log('TEST MODE: Bypassing authentication validation');
      return of({ success: true, message: 'Connection successful (TEST MODE)' }).pipe(delay(500));
    }

    // Normal validation flow (when not in test mode)
    if (!credentials.companyId || !credentials.publicKey || !credentials.privateKey || !credentials.domain) {
      return of({ success: false, message: 'Missing required credentials' }).pipe(delay(500));
    }

    // Simulate API call with 200 response
    return of({ success: true, message: 'Connection successful' }).pipe(delay(1000));
  }

  // API call to test connection with real backend
  testConnectionReal(organizationId: number, integrationId: number | null, credentials: any): Observable<{ success: boolean; message: string }> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails/test_connection/`;

    const requestBody = {
      organization_id: organizationId,
      ticket_integration_user: `${credentials.companyId}+${credentials.publicKey}`,
      ticket_integration_secret: credentials.privateKey,
      ticket_integration_url: credentials.domain,
      ticket_integration_auth_method: 'basic',
      ticket_integration_auth_url: credentials.domain,
      ticket_integration_auth_client_id: this.CONNECTWISE_CLIENT_ID,
      ticket_integration_auth_client_secret: 'string',
      field_map_subject: 'summary',
      field_map_description: 'initialDescription',
      field_map_status_type: 'status',
      field_map_status_type_as_obj: true,
      field_map_priority: 'priority',
      field_map_priority_as_obj: true,
      field_map_return_id: 'id',
      field_map_limit: 'limit',
      field_map_ticket_id: 'id',
      field_map_extra_fields: '',
      field_map_query_fields: null,
      ticket_integration_board: null,
      integration: integrationId,
    };

    return this.http.post<any>(url, requestBody, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    }).pipe(
      map((response) => {
        // If we get a 200 response, treat it as success
        return { success: true, message: 'Connection successful' };
      }),
      catchError((error) => {
        // Let the error propagate to the interceptor
        // The interceptor will show the notification
        throw error;
      }),
    );
  }

  // Generic ticketing test connection — sends form values as-is to the ticketing test endpoint
  testConnectionGeneric(organizationId: number, integrationId: number | null, formValues: any): Observable<{ success: boolean; message: string }> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails/test_connection/`;

    const requestBody = {
      organization_id: organizationId,
      integration: integrationId,
      ticket_integration_auth_method: 'bearer_client_credential',
      ...formValues,
    };

    return this.http.post<any>(url, requestBody, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    }).pipe(
      map(() => {
        return { success: true, message: 'Connection successful' };
      }),
      catchError((error) => {
        throw error;
      }),
    );
  }

  // API call to get boards
  getBoards(organizationId: number): Observable<any[]> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/organizations/${organizationId}/connectwise_boards/`;
    return this.http.get<any[]>(url);
  }

  // API call to get priorities
  getPriorities(organizationId: number): Observable<any[]> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/organizations/${organizationId}/priorities/`;
    return this.http.get<any[]>(url);
  }

  // API call to get statuses for a specific board
  getStatuses(organizationId: number, boardId: number): Observable<any[]> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/organizations/${organizationId}/boards/${boardId}/statutes`;
    return this.http.get<any[]>(url);
  }

  // Mock API call to save all configuration including mappings
  saveConfiguration(config: any): Observable<{ success: boolean; message: string }> {
    // In a real implementation, this would save the complete configuration to the backend
    console.log('Saving ConnectWise configuration:', config);
    return of({ success: true, message: 'Configuration saved successfully' }).pipe(delay(1000));
  }

  getCompanies(organizationId: number, searchQuery?: string): Observable<ConnectWiseCompany[]> {
    let url = `${environment.apiUrl}/v1/ticketintegration/administrative/organizations/${organizationId}/connectwise_companies/?$top=100`;
    if (searchQuery) {
      url += `&q=${encodeURIComponent(searchQuery)}`;
    }
    return this.http.get<ConnectWiseCompany[]>(url);
  }

  getAgreement(organizationId: number, searchQuery?: string, companyId?: number): Observable<ConnectWiseAgreement[]> {
    let url = `${environment.apiUrl}/v1/ticketintegration/administrative/organizations/${organizationId}/connectwise_agreements/?$top=100`;
    if (searchQuery) {
      url += `&q=${encodeURIComponent(searchQuery)}`;
    }
    if (companyId) {
      url += `&company=${companyId}`;
    }
    return this.http.get<ConnectWiseAgreement[]>(url);
  }

  // Toggle test mode on/off
  setTestMode(enabled: boolean): void {
    this.testMode = enabled;
    console.log(`ConnectWise service test mode ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Generic save for ticketing integrations — sends form values as-is
  saveIntegrationDetailsGeneric(organizationId: number, integrationId: number | null, formValues: any): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails/`;

    const requestBody = {
      organization_id: organizationId,
      integration: integrationId || 0,
      ticket_integration_auth_method: 'bearer_client_credential',
      ...formValues,
    };

    return this.http.post<any>(url, requestBody, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    });
  }

  // Generic update for ticketing integrations — sends form values as-is via PUT
  updateIntegrationDetailsGeneric(organizationId: number, integrationId: number | null, formValues: any): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails(${organizationId})/`;

    const requestBody = {
      organization_id: organizationId,
      integration: integrationId || 0,
      ticket_integration_auth_method: 'bearer_client_credential',
      ...formValues,
    };

    return this.http.put<any>(url, requestBody, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000),
    });
  }

  // Save integration details (credentials only)
  saveIntegrationDetails(organizationId: number, integrationId: number | null, credentials: any): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails/`;
    
    const requestBody = {
      organization_id: organizationId,
      integration: integrationId || 0,
      ticket_integration_board: credentials.selectedBoard || null,
      ticket_integration_user: `${credentials.companyId}+${credentials.publicKey}`,
      ticket_integration_secret: credentials.privateKey,
      ticket_integration_url: credentials.domain,
      ticket_integration_auth_method: 'basic',
      ticket_integration_auth_url: credentials.domain,
      ticket_integration_auth_client_id: credentials.clientId || this.CONNECTWISE_CLIENT_ID,
      ticket_integration_auth_client_secret: 'string',
      field_map_subject: 'summary',
      field_map_description: 'initialDescription',
      field_map_status_type: 'status',
      field_map_status_type_as_obj: true,
      field_map_priority: 'priority',
      field_map_priority_as_obj: true,
      field_map_return_id: 'id',
      field_map_limit: 'limit',
      field_map_ticket_id: 'id',
      field_map_extra_fields: '',
      field_map_query_fields: null,
      configuration_status: credentials.configuration_status || undefined,
      is_multi_tenant: credentials.is_multi_tenant !== undefined ? credentials.is_multi_tenant : undefined
    };

    return this.http.post<any>(url, requestBody, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    });
  }
  
  // Get integration details
  getIntegrationDetails(organizationId: number, integrationId?: number | null): Observable<any> {
    let url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails(${organizationId})/`;
    if (integrationId) {
      url += `?integration=${integrationId}`;
    }
    return this.http.get<any>(url);
  }

  // Update integration details (PUT request)
  updateIntegrationDetails(detailPk: number, data: any): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails(${detailPk})/`;
    return this.http.put<any>(url, data, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    });
  }

  // Create integration field values map
  createIntegrationFieldValuesMap(data: any): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationfieldvaluesmap/`;
    return this.http.post<any>(url, data, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    });
  }
  
  // Delete integration field values map (kept for potential future use)
  deleteIntegrationFieldValuesMap(mappingId: number): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationfieldvaluesmap(${mappingId})/`;
    return this.http.delete<any>(url, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    });
  }
  
  // Delete all integration field values map for an organization
  deleteAllIntegrationFieldValuesMap(organizationId: number): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationfieldvaluesmap/organization/${organizationId}/`;
    return this.http.delete<any>(url, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    });
  }
  
  // Get configuration statuses (cached per session via shareReplay)
  getConfigurationStatuses(): Observable<any[]> {
    if (!this.configurationStatuses$) {
      const url = `${environment.apiUrl}/v1/integration/administrative/integration/get_configuration_statuses/`;
      this.configurationStatuses$ = this.http.get<any[]>(url).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.configurationStatuses$;
  }
  
  // Update integration status only
  updateIntegrationStatus(organizationId: number, statusData: { configuration_status: string | number }, integrationId?: number | null): Observable<any> {
    // First get the existing details
    return this.getIntegrationDetails(organizationId, integrationId).pipe(
      switchMap(existingDetails => {
        // Update only the configuration status, ensuring organization_id is included
        const updatedData = {
          ...existingDetails,
          organization_id: organizationId, // Ensure organization_id is always included
          configuration_status: statusData.configuration_status
        };
        
        // Use the integration details pk for the PUT, not orgId
        return this.updateIntegrationDetails(existingDetails.pk, updatedData);
      }),
      catchError(error => {
        console.error('Failed to update integration status:', error);
        throw error;
      })
    );
  }
  
  // Get integration field mappings
  getIntegrationFieldMappings(organizationId: number): Observable<IntegrationFieldMapping[]> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationfieldretrievedvaluesmap(${organizationId})/`;
    console.log('Fetching field mappings from URL:', url);
    console.log('Organization ID:', organizationId);
    
    return this.http.get<IntegrationFieldMapping[] | IntegrationFieldMappingResponse>(url).pipe(
      map(response => {
        console.log('Field mappings API raw response:', response);
        // Handle both array response and object with value property
        let mappings: IntegrationFieldMapping[] = [];
        if (Array.isArray(response)) {
          mappings = response;
        } else if (response && response.value) {
          mappings = response.value;
        }
        console.log('Extracted mappings:', mappings);
        return mappings;
      }),
      catchError(error => {
        console.error('Failed to get integration field mappings:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        console.error('Error details:', error.error);
        console.error('Full URL attempted:', url);
        return of([]);
      })
    );
  }

  // ============================================================
  // Service Type Mapping CRUD
  // ============================================================

  getServiceTypeMappings(integrationDetailsPk: number): Observable<ServiceTypeMappingResponse[]> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationservicetypemap(${integrationDetailsPk})/`;
    return this.http.get<ServiceTypeMappingResponse[] | { value: ServiceTypeMappingResponse[] }>(url).pipe(
      map((response) => {
        if (Array.isArray(response)) return response;
        if (response && response.value) return response.value;
        return [];
      }),
      catchError((error) => {
        console.error('Failed to get service type mappings:', error);
        return of([]);
      })
    );
  }

  createServiceTypeMapping(data: ServiceTypeMappingRequest): Observable<ServiceTypeMappingResponse> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationservicetypemap/`;
    return this.http.post<ServiceTypeMappingResponse>(url, data, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000),
    });
  }

  updateServiceTypeMapping(mappingPk: number, data: ServiceTypeMappingRequest): Observable<ServiceTypeMappingResponse> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationservicetypemap(${mappingPk})/`;
    return this.http.put<ServiceTypeMappingResponse>(url, data, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000),
    });
  }

  deleteServiceTypeMapping(mappingPk: number): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationservicetypemap(${mappingPk})/`;
    return this.http.delete<any>(url, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000),
    });
  }

  // Delete integration configuration
  deleteIntegration(detailPk: number): Observable<any> {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails(${detailPk})/`;
    return this.http.delete<any>(url, {
      context: new HttpContext().set(ERROR_DISPLAY_DURATION, 15000)
    });
  }
}

export interface ConnectWiseCompany {
  id: number;
  name: string;
}

export interface ConnectWiseAgreement {
  id: number;
  name: string;
}

export interface IntegrationFieldMapping {
  pk: number;
  field_name: string;
  one_field_value: string;
  integration_field_value: string;
  integration_field_type: string;
  use_as_default: boolean;
  integration: number;
}

export interface IntegrationFieldMappingResponse {
  "$odata.count": number;
  value: IntegrationFieldMapping[];
}
